import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  CircularProgress,
  TextField,
  Typography,
} from "@mui/material";
import getOrganiserDirectoryAPI from "../../../api/getOrganiserDirectoryAPI";
import routes from "../../../constants/routes";
import colorSchemes from "../../../constants/colorSchemes";

// "Auction Organisers": the companies and people who run auctions on V3Bid.
// There are only a few, so this is a simple card grid - no A-Z or filters.

const BADGE_COLOURS = ["#2D55EB", "#0F766E", "#B45309", "#7C3AED", "#BE185D", "#0369A1"];

const initialsOf = (name = "") =>
  name
    .replace(/[^A-Za-z0-9 ]/g, " ")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0].toUpperCase())
    .join("") || "?";

// the same organiser always gets the same colour
const colourOf = (userId = "") =>
  BADGE_COLOURS[
    [...userId].reduce((sum, char) => sum + char.charCodeAt(0), 0) %
      BADGE_COLOURS.length
  ];

// open the auction list on the tab that has something to show
const statusToOpen = ({ liveCount, upcomingCount }) =>
  liveCount ? "live" : upcomingCount ? "upcoming" : "ended";

const plural = (count, word) => `${count} ${word}${count === 1 ? "" : "s"}`;

const LiveBadge = () => (
  <Box
    sx={{
      display: "inline-flex",
      alignItems: "center",
      gap: 0.75,
      px: 1.25,
      py: 0.25,
      borderRadius: 5,
      backgroundColor: "#FEE2E2",
      color: "#B91C1C",
      fontSize: "12px",
      fontWeight: 700,
    }}
  >
    <Box
      component="span"
      sx={{
        width: 8,
        height: 8,
        borderRadius: "50%",
        backgroundColor: "#DC2626",
        animation: "organiserPulse 1.4s ease-in-out infinite",
        "@keyframes organiserPulse": {
          "0%, 100%": { opacity: 1, transform: "scale(1)" },
          "50%": { opacity: 0.35, transform: "scale(0.8)" },
        },
      }}
    />
    Live now
  </Box>
);

const OrganiserCard = ({ organiser, onView }) => {
  const { companyName, businessType, liveCount, upcomingCount, completedCount } =
    organiser;
  // like BidSpotter: the button counts current auctions (live + upcoming);
  // an organiser with only past auctions gets a "past" button
  const current = liveCount + upcomingCount;
  const counts = [
    liveCount && `${liveCount} live`,
    upcomingCount && `${upcomingCount} upcoming`,
    completedCount && `${completedCount} completed`,
  ].filter(Boolean);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 1.5,
        p: 2.5,
        borderRadius: 3,
        backgroundColor: "#fff",
        border: "1px solid #E5E7EB",
        boxShadow: "0 1px 3px rgba(17, 24, 39, 0.06)",
        transition: "box-shadow .2s, transform .2s",
        "&:hover": {
          boxShadow: "0 8px 24px rgba(45, 85, 235, 0.15)",
          transform: "translateY(-2px)",
        },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
        <Box
          sx={{
            flexShrink: 0,
            width: 52,
            height: 52,
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#fff",
            fontWeight: 700,
            fontSize: "18px",
            backgroundColor: colourOf(organiser.userId),
          }}
        >
          {initialsOf(companyName)}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{ fontWeight: 700, fontSize: "17px", color: "#111827", lineHeight: 1.3 }}
          >
            {companyName}
          </Typography>
          {businessType && (
            <Typography sx={{ fontSize: "13px", color: "#6B7280" }}>
              {businessType}
            </Typography>
          )}
        </Box>
      </Box>

      <Box sx={{ minHeight: 24 }}>{liveCount > 0 && <LiveBadge />}</Box>

      <Typography sx={{ fontSize: "14px", color: "#374151" }}>
        {counts.join(" · ")}
      </Typography>

      <Button
        variant={liveCount ? "contained" : "outlined"}
        onClick={() => onView(organiser)}
        sx={{ mt: "auto", textTransform: "none", fontWeight: 600 }}
      >
        {current
          ? `View auctions (${current}) →`
          : `View past auctions (${completedCount}) →`}
      </Button>
    </Box>
  );
};

function Organisers() {
  const navigate = useNavigate();
  const [organisers, setOrganisers] = useState(null);
  const [failed, setFailed] = useState(false);
  const [search, setSearch] = useState("");

  useEffect(() => {
    getOrganiserDirectoryAPI()
      .then(({ data }) => setOrganisers(data.data || []))
      .catch(() => setFailed(true));
  }, []);

  const shown = useMemo(() => {
    const text = search.trim().toLowerCase();
    if (!organisers || !text) return organisers || [];
    return organisers.filter(({ companyName, businessType }) =>
      `${companyName} ${businessType ?? ""}`.toLowerCase().includes(text)
    );
  }, [organisers, search]);

  const liveNow = (organisers || []).reduce(
    (sum, { liveCount }) => sum + liveCount,
    0
  );

  const viewAuctions = (organiser) =>
    navigate(
      `${routes.auction}?organiser=${organiser.userId}&status=${statusToOpen(organiser)}`,
      { state: { organiserName: organiser.companyName } }
    );

  return (
    <Box sx={{ backgroundColor: "#F5F7FF", minHeight: "70vh", pb: 6 }}>
      <Box
        sx={{
          background: `linear-gradient(135deg, ${colorSchemes.primaryColor || "#2D55EB"} 0%, #1E3A8A 100%)`,
          color: "#fff",
          px: 2,
          py: { xs: 5, md: 7 },
          textAlign: "center",
        }}
      >
        <Typography
          component="h1"
          sx={{ fontSize: { xs: "28px", md: "38px" }, fontWeight: 800, mb: 1 }}
        >
          Auction Organisers
        </Typography>
        <Typography sx={{ fontSize: { xs: "15px", md: "17px" }, opacity: 0.9 }}>
          The companies and individuals who run auctions on V3Bid
        </Typography>
        {organisers && (
          <Typography sx={{ mt: 2, fontWeight: 600 }}>
            {plural(organisers.length, "organiser")} ·{" "}
            {plural(liveNow, "auction")} live now
          </Typography>
        )}
      </Box>

      <Box sx={{ maxWidth: 1140, mx: "auto", px: 2, mt: 4 }}>
        {organisers && organisers.length > 9 && (
          <TextField
            size="small"
            label="Search organisers"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            sx={{ mb: 3, width: { xs: "100%", sm: 320 }, backgroundColor: "#fff" }}
          />
        )}

        {failed ? (
          <Typography sx={{ textAlign: "center", color: "#6B7280", py: 6 }}>
            Could not load organisers. Please try again later.
          </Typography>
        ) : !organisers ? (
          <Box sx={{ textAlign: "center", py: 6 }}>
            <CircularProgress />
          </Box>
        ) : shown.length === 0 ? (
          <Typography sx={{ textAlign: "center", color: "#6B7280", py: 6 }}>
            {organisers.length ? "No organiser matches your search." : "No organisers yet."}
          </Typography>
        ) : (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, 1fr)",
                md: "repeat(3, 1fr)",
              },
              gap: 3,
            }}
          >
            {shown.map((organiser) => (
              <OrganiserCard
                key={organiser.userId}
                organiser={organiser}
                onView={viewAuctions}
              />
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
}

export default Organisers;
